import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function ProgressBar({ pct, large = false }) {
    return (
        <div className={"lt-progress-bar" + (large ? " lt-progress-bar--lg" : "")} data-testid="portal-cl-progress">
            <div className="lt-progress-bar__fill" style={{ width: `${pct}%` }} />
        </div>
    );
}
function StatusChip({ pct }) {
    if (pct >= 100) return <span className="lt-chip lt-chip--ok" data-testid="portal-cl-status">Completata</span>;
    if (pct > 0)   return <span className="lt-chip lt-chip--wip" data-testid="portal-cl-status">In corso</span>;
    return <span className="lt-chip lt-chip--inactive" data-testid="portal-cl-status">Non avviata</span>;
}

export function PortalChecklistList() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState(null);
    const [cats, setCats] = useState([]);
    const [catFilter, setCatFilter] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: 1, page_size: 100 };
            if (catFilter) params.category_id = catFilter;
            const { data } = await api.get("/client/checklist", { params });
            setItems(data.items || []);
        } catch { toast.error("Errore caricamento checklist"); }
        finally { setLoading(false); }
    }, [catFilter]);

    useEffect(() => {
        api.get("/client/checklist/counters").then(({ data }) => setCounters(data)).catch(() => {});
        api.get("/client/checklist-categories").then(({ data }) => setCats(data.items || [])).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const counterCards = useMemo(() => {
        // in_corso/completate calcolati da items (visibili), non da counters (che espone solo totali+per_categoria)
        return [
            { key: "totali", label: "Totali", value: counters?.totali ?? 0 },
            { key: "in_corso", label: "In corso", value: items.filter(i => i.progress_percent > 0 && i.progress_percent < 100).length },
            { key: "completate", label: "Completate", value: items.filter(i => i.progress_percent >= 100).length },
        ];
    }, [counters, items]);

    return (
        <div style={{ padding: "32px 40px", maxWidth: 1100 }} data-testid="portal-cl-page">
            <h1 className="lt-h1" style={{ marginBottom: 8 }}>Checklist</h1>
            <p className="lt-sub" style={{ marginBottom: 24 }}>Attività condivise da ENERLEFT.</p>

            <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20 }} data-testid="portal-cl-counters">
                {counterCards.map((c) => (
                    <div key={c.key} className="lt-counter-card" style={{ cursor: "default" }} data-testid={`portal-cl-counter-${c.key}`}>
                        <div className="lt-counter-card__value">{c.value}</div>
                        <div className="lt-counter-card__label">{c.label}</div>
                    </div>
                ))}
            </div>

            {cats.length > 0 && (
                <div className="lt-segmented" style={{ marginBottom: 16 }} data-testid="portal-cl-filter-cat">
                    <button type="button" className={"lt-segmented__btn" + (catFilter === "" ? " is-active" : "")} onClick={() => setCatFilter("")}>Tutte</button>
                    {cats.map((c) => (
                        <button key={c.id} type="button" className={"lt-segmented__btn" + (catFilter === c.id ? " is-active" : "")} onClick={() => setCatFilter(c.id)}>{c.name}</button>
                    ))}
                </div>
            )}

            {loading ? (
                <div style={{ color: "#8A8F88" }}>Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="lt-card" style={{ padding: 40, textAlign: "center" }} data-testid="portal-cl-empty">
                    <p className="lt-sub">Nessuna checklist condivisa al momento.</p>
                </div>
            ) : (
                <div className="lt-doc-card-list" data-testid="portal-cl-list">
                    {items.map((it) => (
                        <div key={it.id} className="lt-panel" style={{ cursor: "pointer" }} onClick={() => nav(`/portal/checklist/${it.id}`)} data-testid={`portal-cl-row-${it.id}`}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                                <span style={{ fontWeight: 600, color: "#181B20", fontSize: 15 }}>{it.title}</span>
                                <span className="lt-chip lt-chip--inactive">{it.category?.name || "—"}</span>
                                <StatusChip pct={it.progress_percent} />
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <span style={{ fontSize: 13, color: "#5F6560", fontWeight: 500, minWidth: 44 }}>{it.items_done}/{it.items_total}</span>
                                <div style={{ flex: 1 }}><ProgressBar pct={it.progress_percent} /></div>
                                <span style={{ fontSize: 12, color: "#8A8F88" }}>{it.progress_percent}%</span>
                            </div>
                            <div style={{ fontSize: 12, color: "#8A8F88", marginTop: 8 }}>Aggiornata {fmtDate(it.updated_at)}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function PortalChecklistDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/client/checklist/${id}`).then(({ data }) => setData(data))
            .catch(() => { toast.error("Checklist non trovata"); nav("/portal/checklist"); });
    }, [id, nav]);

    if (!data) return <div style={{ padding: 40, color: "#8A8F88" }}>Caricamento…</div>;

    return (
        <div style={{ padding: "32px 40px", maxWidth: 900 }} data-testid="portal-cl-detail-page">
            <Link to="/portal/checklist" className="lt-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16 }}><ArrowLeft size={14} /> Torna alle checklist</Link>
            <h1 className="lt-h1" data-testid="portal-cl-detail-title">{data.title}</h1>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, marginBottom: 16, flexWrap: "wrap" }}>
                <span className="lt-chip lt-chip--inactive">{data.category?.name || "—"}</span>
                <StatusChip pct={data.progress_percent} />
                <span className="lt-sub" style={{ fontSize: 13 }}>{data.items_done}/{data.items_total} · {data.progress_percent}%</span>
            </div>
            <ProgressBar pct={data.progress_percent} large />
            <div className="lt-card" style={{ padding: 8, marginTop: 20 }} data-testid="portal-cl-items">
                {data.items.map((it) => (
                    <div key={it.id} className="lt-item-row" data-testid={`portal-cl-item-${it.id}`}>
                        <div style={{ width: 24 }} />
                        <span
                            className={"lt-checkbox lt-checkbox--disabled" + (it.completed ? " is-checked" : "")}
                            aria-disabled="true"
                            data-testid={`portal-cl-item-check-${it.id}`}
                        >
                            {it.completed && <Check size={12} strokeWidth={3} color="#181B20" />}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={it.completed ? { textDecoration: "line-through", color: "#8A8F88" } : { color: "#181B20" }}>{it.text}</div>
                            {it.completed && it.completed_at && <div style={{ fontSize: 12, color: "#8A8F88", marginTop: 2 }}>Completato il {fmtDateTime(it.completed_at)}</div>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
